import {Skeleton, view, Action, Icon} from '@sail/ui';
import {useSelector, setValue} from 'src/v2/state/store';
import {WalletCard} from 'src/v2/components/wallet/WalletCard';
import {ErrorBanner} from 'src/v2/components/error/ErrorBanner';
import {Text} from 'src/v2/components/common';
import {useInitializeExchangePayment} from 'src/v2/components/exchangeScreen/useInitializeExchangePayment';
import {ExchangeTransactionProcessing} from 'src/v2/components/exchangeScreen/ExchangeTransactionProcessing';
import {cancel, external} from '@sail/icons/react/Icon';
import {QrCode} from 'src/v2/components/common/QrCode';
import {BrandedButton} from 'src/v2/components/common/BrandedButton';
import {safeWindowOpen} from '@stripe-internal/safe-links';
import {isMobile} from 'src/utils/utils';
import {ScreenContentLayout} from 'src/v2/components/screen/ScreenContentLayout';
import {useIsAnyCryptoWalletConnectFlow} from 'src/v2/components/anyCrypto/useIsAnyCryptoWalletConnectFlow';

import {message, useIntl} from '@sail/intl';

const messages = {
  title: message<{provider: string}>({
    id: 'crypto.payins.v2.exchange_screen.title',
    description:
      'Title for the exchange screen. {provider} is the name of the exchange provider (e.g. "Crypto.com")',
    defaultMessage: 'Pay with {provider}',
  }),

  description: message({
    id: 'crypto.payins.v2.exchange_screen.description',
    description: 'Description for Crypto.com',

    defaultMessage:
      'Scan this QR code from your Crypto.com app to process your payment.',
  }),

  testModeMessage: message({
    id: 'crypto.payins.v2.exchange_screen.test_mode_message',
    description: 'Message shown when QR code is not available in test mode',
    defaultMessage: 'QR code is not available in testmode',
  }),

  continueToPayment: message({
    id: 'crypto.payins.v2.exchange_screen.continue_to_payment',
    description: 'Button text to continue to payment in test mode',
    defaultMessage: 'Continue to payment',
  }),

  connectButton: message({
    id: 'crypto.payins.v2.exchange_screen.connect_button',
    description: 'Button text to pay with exchange on mobile',
    defaultMessage: 'Pay with Crypto.com',
  }),
};

const QR_CODE_SIZE = 266;

// False positive: the onclick is synchronously opening a new window.
// nosemgrep: no-async-window-open
export const ExchangeScreen = () => {
  const intl = useIntl();

  const {paymentUrl, paymentQrCode} = useInitializeExchangePayment();
  const isAnyCryptoWalletConnectFlow = useIsAnyCryptoWalletConnectFlow();

  const exchangeOption = useSelector((state) => state.exchangeOption);
  const livemode = useSelector((state) => state.livemode);
  const sessionState = useSelector((state) => state.state);
  const isMobileDevice = isMobile();
  const mobileDeeplink = `monaco://pay?payment_url=${paymentQrCode}`;

  if (!exchangeOption) {
    throw new Error('exchangeOption is required');
  }

  const {name, iconUrl} = exchangeOption;
  // It takes some time for the redirect to complete when the session becomes 'fulfillment_complete',
  // so we continue show the processing state until then.
  const isTransactionProcessing =
    sessionState === 'purchase_complete' ||
    sessionState === 'fulfillment_complete';

  if (isTransactionProcessing) {
    return (
      <ScreenContentLayout>
        <ExchangeTransactionProcessing />
        <ErrorBanner />
      </ScreenContentLayout>
    );
  }

  return (
    <ScreenContentLayout
      onBack={
        isAnyCryptoWalletConnectFlow
          ? () =>
              setValue((state) => {
                state.screen = 'WALLET_SELECT';
              })
          : undefined
      }
    >
      <WalletCard
        wallet={{
          option: exchangeOption,
        }}
        bottomContent={
          isMobileDevice ? (
            <view.div
              css={{
                stack: 'y',
                alignX: 'center',
                alignY: 'center',
                gap: 'medium',
              }}
            >
              {mobileDeeplink ? (
                <BrandedButton
                  onClick={() => {
                    safeWindowOpen(
                      mobileDeeplink,
                      '_self',
                      {},
                      {allowedProtocols: ['monaco:']},
                    );
                  }}
                  css={{
                    borderRadius: '8px',
                    width: 'fill',
                  }}
                >
                  <view.div
                    css={{
                      stack: 'x',
                      alignX: 'center',
                      alignY: 'center',
                      gap: 'xsmall',
                    }}
                  >
                    {intl.formatMessage(messages.connectButton)}
                    <Icon icon={external} size="xsmall" />
                  </view.div>
                </BrandedButton>
              ) : (
                <Skeleton css={{width: 'fill', height: 'space.500'}} />
              )}
            </view.div>
          ) : (
            <view.div
              css={{
                stack: 'y',
                alignX: 'center',
                alignY: 'center',
                padding: '24px 12px',
                gap: 'medium',
              }}
            >
              <view.div
                css={{
                  stack: 'y',
                  gap: 'xsmall',
                  width: QR_CODE_SIZE,
                  alignX: 'center',
                }}
              >
                <Text type="heading" css={{color: 'text'}}>
                  {intl.formatMessage(messages.title, {provider: name})}
                </Text>
                <Text type="body" css={{textAlign: 'center'}}>
                  {intl.formatMessage(messages.description)}
                </Text>
              </view.div>
              {paymentUrl && paymentQrCode ? (
                <view.div css={{stack: 'z'}}>
                  <view.div
                    css={
                      !livemode
                        ? {
                            filter: 'blur(6px)',
                            opacity: 0.24,
                          }
                        : undefined
                    }
                  >
                    <QrCode
                      qrCodeUri={paymentQrCode}
                      iconUrl={iconUrl}
                      name={name}
                    />
                  </view.div>
                  {/* Crypto.com does not support payments in app in test mode */}
                  {!livemode ? (
                    <view.div
                      css={{
                        alignSelfX: 'center',
                        alignSelfY: 'center',
                        stack: 'y',
                        gap: 'small',
                        alignX: 'center',
                        paddingX: 'medium',
                        zIndex: 99,
                      }}
                    >
                      <Text
                        type="caption"
                        css={{
                          color: 'text',
                          textAlign: 'center',
                          width: '292px',
                        }}
                      >
                        {intl.formatMessage(messages.testModeMessage)}
                      </Text>
                      <BrandedButton
                        onClick={() => {
                          safeWindowOpen(paymentUrl, '_blank');
                        }}
                        css={{
                          padding: '8px 12px',
                          borderRadius: '8px',
                          height: '32px',
                          width: '145px',
                        }}
                        textType="caption"
                        textCss={{fontWeight: '510'}}
                      >
                        {intl.formatMessage(messages.continueToPayment)}
                      </BrandedButton>
                    </view.div>
                  ) : null}
                </view.div>
              ) : (
                <Skeleton css={{width: '266px', height: '266px'}} />
              )}
            </view.div>
          )
        }
        headerActions={
          isAnyCryptoWalletConnectFlow ? undefined : (
            <Action
              onPress={() => {
                setValue((state) => {
                  state.screen = 'WALLET_SELECT';
                });
              }}
            >
              <Icon icon={cancel} size="xsmall" />
            </Action>
          )
        }
      />
      <ErrorBanner />
    </ScreenContentLayout>
  );
};
